import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as d3 from 'd3';
import ReactMarkdown from 'react-markdown';
import * as htmlToImage from 'html-to-image';
import Search from './Search';
import {
  getServerBookUrlFromUrl,
  useOpenBook,
  updateLinkTitles,
} from '../util/util';

// --- DATA SOURCES ---

const defaultMindMapData = {
  name: "A Simple Mindmap of Dr. B.R. Ambedkar's Writings and Speeches",
  children: [
    {
      name: 'Life & Philosophy',
      children: [
        {
          name: 'Timeline',
          children: [
            { name: 'Born: April 14, 1891' },
            { name: 'Died: December 6, 1956' },
            { name: '60th Birthday Celebration: Oct 28, 1954' },
            { name: 'Diamond Jubilee Celebration Committee' },
          ],
        },
        {
          name: 'Influences & Worship',
          children: [
            { name: 'Gautam Buddha' },
            { name: 'Kabir' },
            { name: 'Mahatma Phule' },
            { name: 'Learning' },
            { name: 'Self-Respect' },
            { name: 'Character' },
          ],
        },
        {
          name: 'Core Beliefs',
          children: [
            { name: 'Bread better than God worship' },
            { name: 'Equality in Hindu religion' },
            { name: 'Chaturvarnya must be rooted out' },
            { name: 'End privileges for higher classes' },
            { name: 'Unity is crucial' },
            { name: 'Importance of self-study' },
            { name: 'Followers of Buddha, Kabir, Phule' },
          ],
        },
        {
          name: 'Critique of Hinduism',
          children: [
            { name: 'Untouchability as concrete inequality' },
            { name: "Worst than leper's condition" },
            { name: 'Nausea and hatred for Untouchables' },
            { name: 'Stigma on Hindu religion (rejected as meaningless)' },
            { name: 'Hinduism cherishes wrong ideals' },
            { name: 'Hindu philosophy: Brahma remained intellect, not social' },
            { name: 'Caste system: degenerate Chaturvarnya' },
            { name: 'Caste is inherent vice, not external' },
          ],
        },
        {
          name: 'Emancipation of Mind & Soul',
          children: [
            { name: 'Necessary for political expansion' },
            { name: 'Political revolutions preceded by social/religious' },
            {
              name: 'Examples: Luther, Puritanism, Prophet Mohammad, Buddha, Shivaji, Guru Nanak',
            },
          ],
        },
      ],
    },
    {
      name: 'Hindu Social System & Caste',
      children: [
        {
          name: 'Essence of Caste',
          children: [
            { name: 'Prohibition of intermarriage (Endogamy)' },
            {
              name: 'Difference from other endogamous groups (e.g., Negroes/Whites)',
            },
            {
              name: 'In India: homogeneous population, caste is unique problem',
            },
          ],
        },
        {
          name: 'Caste Formation (Imitation)',
          children: [
            { name: 'Non-Brahmin classes imitate customs' },
            { name: 'Self-duplication inherent' },
            { name: 'Excommunication for violating code' },
            { name: 'New castes formed by mechanical law' },
          ],
        },
        {
          name: 'Chaturvarnya',
          children: [
            { name: 'Ideal system rejected' },
            { name: 'Vicious system' },
            { name: 'Division of labor (Brahmin, Kshatriya, Vaishya, Shudra)' },
            { name: 'Labels perpetuate hierarchy by birth' },
            { name: 'Incompatible with united India ideal' },
            { name: 'Individual and social folly/crime' },
            { name: 'Responsible for decay of Hindu society' },
          ],
        },
        {
          name: 'Caste in Non-Hindus vs. Hindus',
          children: [
            { name: 'Non-Hindus: fundamentally different' },
            { name: 'Non-Hindus: many ties hold together' },
            { name: 'Non-Hindus: caste has no same social significance' },
            { name: 'Non-Hindus: excommunication foreign concept' },
            { name: 'Hindus: caste has religious consecration' },
            { name: 'Hindus: religion compels isolation/segregation' },
          ],
        },
        {
          name: 'Destruction of Caste',
          children: [
            {
              name: "Falls under 'third category' of reform (opposed to religious principles)",
            },
            { name: 'Requires destroying sacredness/divinity of caste' },
            { name: 'Means destroying authority of Shastras and Vedas' },
          ],
        },
        {
          name: 'Manu Smriti & Shudras',
          children: [
            { name: "Shudra's name: contemptible" },
            { name: "Shudra's second name: denoting service" },
            { name: 'Shudra wealth collection prohibited' },
            { name: 'Shudra insults: tongue cut out' },
            { name: 'Shudra physical harm to higher castes: limb cut off' },
            { name: 'Shudra on same seat as high caste: branded/banished' },
            { name: "King's duty to ensure Shudras discharge tasks" },
          ],
        },
        {
          name: 'Caste Origin Theories',
          children: [
            { name: 'Vedas: Purusha, Manu, Prajapati, Vratya, Soma' },
            { name: 'Brahmanas: vacillate between Prajapati and Brahma (new)' },
            {
              name: 'Taittiriya Brahmana: Brahmins from Gods, Shudras from Asuras',
            },
            {
              name: 'Purusha Sukta: later addition, question-answer form for Varna origin',
            },
            { name: 'Aryan Race Theory: Varna means color (disputed)' },
          ],
        },
      ],
    },
    {
      name: 'Untouchability & Depressed Classes',
      children: [
        {
          name: 'Conditions & Discrimination',
          children: [
            { name: 'Untouchability as concrete inequality' },
            { name: 'Worse condition than lepers' },
            { name: 'Villages: separate quarters (Maharwada, Mangwada)' },
            { name: 'Economic dependence on high castes' },
            { name: 'Perpetual war in villages' },
            { name: 'Lack of police/magistrate protection' },
            { name: 'Lynch Law against Harijans (Chavadi Courts)' },
            {
              name: 'Boycott for civic rights, clean clothes, tiled roofs, schooling',
            },
            { name: 'Compelled to carry dead animals, eat carrion' },
            { name: 'Objection to changing religion or respectable names' },
            {
              name: 'Objection to entering professions, gaining authority, buying land',
            },
            { name: 'Hindu religion and teachings are main cause' },
            {
              name: 'Comparison to slavery: legally free, socially no freedom',
            },
          ],
        },
        {
          name: 'Struggle for Rights',
          children: [
            { name: 'Mahad Satyagraha (1927): Chawdar Tank entry' },
            { name: 'Nasik Satyagraha: gained political status' },
            {
              name: 'Hyderabad State Scheduled Castes Federation: demanded community independence',
            },
            {
              name: 'Bengal Provincial Scheduled Castes Federation: advocated for education, appointments',
            },
            { name: 'Scheduled Castes Federation (political party) formed' },
            { name: 'Secured reserved seats in Assemblies/Parliament' },
            { name: 'U.P. Scheduled Castes Federation: Lucknow Satyagraha' },
          ],
        },
        {
          name: 'Political Representation',
          children: [
            { name: 'Southborough Committee ignored them' },
            {
              name: 'Government of India (1919) commentary: insufficient representation',
            },
            {
              name: 'All-India Depressed Classes Congress (1930): advocated Ambedkar for RTC',
            },
            {
              name: 'Round Table Conference: Ambedkar demanded distinct political recognition',
            },
            {
              name: 'Communal Award (1932): recognized as separate from Hindus',
            },
            {
              name: 'Poona Pact (1932): admitted main contention for separate political recognition',
            },
            { name: 'Joint electorates & reserved seats (compromise)' },
            {
              name: "Gandhi's opposition to separate electorates (disputed as silly/insincere)",
            },
            {
              name: "Viceroy's Declaration (1940): Muslims & Untouchables distinct",
            },
            {
              name: '1937 Provincial Elections: Congress nominated Untouchables',
            },
            {
              name: 'Cabinet Mission (1946): ignored Scheduled Castes, brought disgrace',
            },
            { name: 'Demand for 50% representation in Interim Government' },
          ],
        },
        {
          name: 'Anti-Untouchability Campaign (Gandhi)',
          children: [
            { name: 'Initial support (Young India 1920)' },
            {
              name: 'All-India Congress Committee (1922): formed committee, allotted funds',
            },
            { name: 'Work left to Hindu Mahasabha (militant, political)' },
            { name: 'Project failed due to lack of financial provision' },
            {
              name: "Gandhi's R.T.C. argument: 'unkindest cut', claimed to represent Untouchables",
            },
            {
              name: "Ambedkar's view: Gandhi insincere, only fought for Hindu control",
            },
            {
              name: "Temple Entry Bill: propaganda against spiritual fads, 'bread better than God'",
            },
            { name: 'Untouchables not keen on temple entry' },
            {
              name: "Gandhi's fasts: for Guruvayur Temple, but failed to enforce",
            },
            {
              name: 'Harijan Sevak Sangh: management passed to Congress Hindus',
            },
            {
              name: "Gandhi's doctrine: welfare work for Untouchables is Hindu penance",
            },
            {
              name: 'Failure of campaign evident (e.g., Bombay Presidency temples)',
            },
          ],
        },
        {
          name: "Sikhism Conversion (Ambedkar's Consideration)",
          children: [
            { name: 'Hindu Sabha leaders believed Sikhism not alien' },
            { name: 'Sikhs and Hindus intermarried' },
            { name: 'Ramdasis (Sikhs) classified as Scheduled Castes' },
          ],
        },
      ],
    },
    {
      name: 'Political & Constitutional Thought',
      children: [
        {
          name: 'Indian Federation vs. Freedom (1939)',
          children: [
            { name: 'Scheme full of defects' },
            { name: 'Two attitudes: accept & work, or demand changes' },
            { name: 'British India paid price to Princes for joining' },
            { name: 'British India did not get real responsible government' },
            { name: 'Lost claim for Dominion Status in own right' },
            { name: 'Struggle of British India led to constitution' },
          ],
        },
        {
          name: 'Parliamentary Democracy',
          children: [
            { name: 'Midway between autocracy & dictatorship of proletariat' },
            { name: 'Delicate institution' },
            { name: 'Government by law' },
            { name: 'Rule of law: makers of law are below law' },
          ],
        },
        {
          name: 'Provincial Autonomy',
          children: [
            { name: 'Responsible government must be autonomous' },
            { name: 'Demarcate spheres of Provincial & Central Govts' },
            {
              name: 'Lucknow Pact defect: did not consider effect on other interests',
            },
            { name: 'Lucknow Pact led to universal dissatisfaction' },
            { name: 'Wrongly settled shall be re-settled' },
          ],
        },
        {
          name: 'Public Services & Representation',
          children: [
            { name: 'Public Service Commission for recruitment/control' },
            { name: 'Secure adequate representation of all communities' },
            { name: 'Nomination system condemned (mockery of representation)' },
            { name: 'Nominated non-officials ineligible for ministership' },
          ],
        },
        {
          name: 'Hindu Code Bill (1947-1951)',
          children: [
            { name: 'Ambedkar as Law Minister' },
            { name: 'Aim: amend & codify Hindu Law' },
            { name: 'Controversial clauses: marriage, divorce, succession' },
            { name: 'Equality for women, ending sex discrimination' },
            { name: 'Opposed by large sections (vested interests)' },
            { name: 'Support for inter-caste, inter-communal marriages' },
            { name: 'Divorce: necessary for incompatible couples' },
            {
              name: "Smritis recognize women's property rights (denied in practice)",
            },
            { name: 'Desire for uniform Civil Code for all India' },
            { name: 'Ambedkar resigned over its failure to pass' },
          ],
        },
        {
          name: 'Constitution of India (Architect)',
          children: [
            { name: 'Chairman of Drafting Committee' },
            { name: 'Ensures security, stability, progress' },
            { name: 'Promotes collective good, individual personality' },
            { name: 'Ideal: political and economic democracy' },
            {
              name: 'Amendment process: two groups (Parliamentary majority, or with State ratification)',
            },
            {
              name: 'Fundamental Rights: absolute (American model) with inherent police power for limitations',
            },
            {
              name: "President bound by Council of Ministers' advice (Article 61)",
            },
            { name: 'Governor to be nominated (not elected)' },
          ],
        },
        {
          name: 'Pakistan Demand',
          children: [
            { name: 'Muslim case: ethnically homogeneous admin areas' },
            { name: 'Muslims as separate nation, desire national home' },
            { name: 'Hindus treat Muslims as second-class citizens' },
            { name: 'Islamic culture deeply deposited in North India' },
            {
              name: 'Frees Hindus from Muslim turbulence (predominant partners)',
            },
            { name: "Hindus' blame on British 'divide and rule' facile" },
            {
              name: 'Muslims exploit Hindu weaknesses (e.g., separate electorates)',
            },
            { name: 'Insistence on separate electorates a bargaining tool' },
            {
              name: 'Alternative: mixed party of Hindus & Muslims for social/economic regeneration',
            },
            { name: 'Common cause with lower-caste Hindus' },
          ],
        },
      ],
    },
    {
      name: 'Economic & Financial Reforms',
      children: [
        {
          name: 'Land Taxation',
          children: [
            { name: 'Object: cease taxing uneconomic holdings' },
            { name: 'Graded tax on larger agricultural incomes' },
            { name: 'Alienated lands: annual loss to province' },
            { name: 'Revenue largely utilized for actual tillers' },
          ],
        },
        {
          name: 'Small Holdings & Industrialization',
          children: [
            { name: 'Indian agriculture problem: uneconomic holdings' },
            { name: 'Industrialization is soundest remedy' },
            { name: 'Lessens pressure on land' },
            { name: 'Increases capital & capital goods' },
            { name: 'Forces enlargement of holdings' },
          ],
        },
        {
          name: 'Provincial Finance (British India)',
          children: [
            { name: 'Separation of Imperial & Local Finance (1855)' },
            { name: 'Local Funds: district-specific & province-wide' },
            { name: 'Burma experience: need for elasticity in revenues' },
            {
              name: 'Three categories of Accounts: Imperial, Provincial, Joint',
            },
            { name: 'Grants-in-aid as corrective for provincial negligence' },
            { name: 'Land revenue & irrigation: wholly provincial receipts' },
            { name: 'Income tax: Indian receipt (uniform rate)' },
            {
              name: 'Permanent Settlement: rejected universally, fetter on popular govt',
            },
          ],
        },
        {
          name: 'Currency System',
          children: [
            { name: 'Indian currency reform needed' },
            { name: 'From double standard to silver standard' },
            {
              name: 'Falling exchange: bounty on wage-earner, loss to fixed incomes',
            },
            { name: 'Bimetallic basis initially' },
            {
              name: 'Gold standard: stable prices, link rupee to gold at 1s. 6d.',
            },
            {
              name: 'Gold-exchange standard: reserve out of rupee coinage profits',
            },
            { name: 'Diseased currency (1893 mint closing)' },
          ],
        },
        {
          name: 'Money-lending Licensing',
          children: [
            { name: 'Annual license required for business' },
            { name: 'License in own name, void if otherwise' },
            { name: 'No accumulated interest converted to principal' },
            { name: 'Monthly simple interest on principal outstanding' },
          ],
        },
      ],
    },
    {
      name: 'Social Issues & General Works',
      children: [
        {
          name: 'Education',
          children: [
            { name: 'University reform: promote research & higher education' },
            { name: 'Avoid examination system as be-all and end-all' },
            {
              name: 'Post-graduate work (Sociology & Economics) by University',
            },
            { name: 'Rivalry between University & College professoriate' },
          ],
        },
        {
          name: 'Labour & Employment',
          children: [
            { name: 'Breach of contract of service: not a crime, civil wrong' },
            { name: 'Penalizing strike = making worker a slave' },
            {
              name: 'Prohibition of women underground in mines (ban withdrawn 1943)',
            },
            { name: 'Contract system for labour in Delhi factories' },
            { name: 'Bevin Training Scheme for Indian technicians' },
          ],
        },
        {
          name: 'Water Resources & Development',
          children: [
            { name: 'Damodar Valley Scheme (1945)' },
            { name: "Utilize water resources for everyone's advantage" },
          ],
        },
        {
          name: 'Hindu-Muslim Relations',
          children: [
            {
              name: 'Communal jealousies, riots (Multan, Kohat, Delhi, Nagpur, Lahore, Lucknow)',
            },
            { name: 'Muslim exploitation of Hindu weaknesses' },
            { name: 'Muslims insist on separate electorates as bargain' },
          ],
        },
        {
          name: "Buddhism (Dr. Ambedkar's Interpretations)",
          children: [
            { name: 'Religion for a free Society' },
            { name: 'Not every religion is worth having' },
            { name: "No God or Soul in Buddha's religion" },
            { name: 'Principal task: emancipate depressed from sorrow' },
            {
              name: 'Differs from other religions: not about man with God, no creation by God',
            },
            { name: 'Dhamma: recognize suffering, show way to remove it' },
            { name: 'Worth, not birth, is measure of man' },
            { name: 'Promote equality' },
            {
              name: 'Cleanse mind of impurities (covetousness, passion, ignorance)',
            },
            { name: 'Ahimsa: not be-all and end-all, but vast teachings' },
            { name: 'Rebirth without transmigration' },
            { name: 'No soul' },
            {
              name: 'Elevation of Shudras & Women: Buddhist order open to all',
            },
            {
              name: 'Marriage as contract, women could acquire property/learning',
            },
            { name: "Called 'Shudra religion' by enemies" },
            {
              name: 'Decline in India: slaughter of Buddhist monks by Islamic invaders',
            },
          ],
        },
        {
          name: 'Writings and Speeches (Volumes)',
          children: [
            {
              name: 'Vol. 1: Castes in India, Annihilation of Caste, Ranade, Gandhi & Jinnah',
            },
            {
              name: 'Vol. 2: In the Bombay Legislature, With Simon Commission, At RTC',
            },
            {
              name: 'Vol. 3: Philosophy of Hinduism, India & Pre-Requisites of Communism',
            },
            { name: 'Vol. 4: Riddles in Hinduism' },
            { name: "Vol. 5: Untouchables or Children of India's Ghetto" },
            {
              name: 'Vol. 6: Administration & Finance of East India Company, Problem of Rupee',
            },
            { name: 'Vol. 7: The Shudras' },
            { name: 'Vol. 8: Pakistan or the Partition of India' },
            {
              name: 'Vol. 9: What Congress and Gandhi Have Done to the Untouchables',
            },
            {
              name: "Vol. 10: As Member of Governor-General's Executive Council",
            },
            { name: 'Vol. 11: The Buddha and His Dhamma' },
            {
              name: 'Vol. 12: Ancient Indian Commerce, Lectures on English Constitution',
            },
            { name: 'Vol. 13: Principal Architect of Constitution of India' },
            { name: 'Vol. 14: Hindu Code Bill (Parts 1 & 2)' },
            { name: "Vol. 15: As Free India's First Law Minister" },
            { name: 'Vol. 16: Pali Grammar, Pali Dictionary' },
            {
              name: 'Vol. 17: Dr. B.R. Ambedkar and His Egalitarian Revolution (Parts 1, 2, 3)',
            },
          ],
        },
      ],
    },
  ],
};

const mindMapSources = [
  {
    name: 'Simple Overview',
    id: 'default',
    description: `
A summary of Dr. B.R. Ambedkar's key writings and philosophies, focusing on social reform and justice.
- **Includes:** Life, Philosophy, and Caste System.
- **Source:** Pre-loaded with the application.
    `,
    data: defaultMindMapData,
  },
  {
    name: 'Deep Dive with Dr. Sairam',
    id: 'sairam-deep-dive',
    description: `
- An in-depth exploration of Dr. B.R. Ambedkar's works, curated by Dr. Sairam, covering various aspects of his contributions.
- Dr. Sairam is a Dentist by profession and an independent researcher. He writes articles exploring the fascinating lives of Dr. Ambedkar, Phule couple and the Ambedkarite movement.
To watch his video on overview of Dr. Ambedkar's life and work, [click here](https://www.youtube.com/watch?v=wJtO8EIv67k).
    `,
    url: 'https://baws.in/assets/mindmaps/sairam-deep-dive.json',
  },
  {
    name: 'Navigating Buddha and His Dhamma',
    id: 'navigating-buddha-and-his-dhamma',
    description: `
- An in-depth exploration of Dr. B.R. Ambedkar's works, focusing on his interpretation of Buddhism and its relevance to social justice.
- This section delves into the key themes and ideas presented in "Buddha and His Dhamma".
    `,
    url: 'https://baws.in/assets/mindmaps/navigating-buddha-and-his-dhamma.json',
  },
];

// --- MOCK API & HELPERS ---
const fetchMindMapFromUrl = async (url) => {
  try {
    const response = await fetch(url, { mode: 'cors' });
    if (!response.ok) {
      throw new Error(`Network response was not ok: ${response.statusText}`);
    }
    const data = await response.json();
    // A simple validation to ensure the fetched data looks like a mind map
    if (!data.name || !data.children) {
      throw new Error('Fetched data is not in the correct mind map format.');
    }
    return data;
  } catch (error) {
    console.error('Failed to fetch mind map from URL:', error);
    // Return a default error structure or re-throw to be handled by the caller
    throw error;
  }
};

const formatLinks = (links) => {
  if (!links || links.length === 0) {
    return '';
  }
  if (links.length === 1) {
    return `\n\n[Source](${links[0]})`;
  }
  const mainLink = `[Source](${links[0]})`;
  const additionalLinks = links
    .slice(1)
    .map((url, index) => {
      return `[${index + 2}](${url})`;
    })
    .join(', ');
  return `\n\n${mainLink} (also see here ${additionalLinks})`;
};

const fetchMarkdownFromLambda = async (nodeData, contextPath) => {
  await new Promise((res) => setTimeout(res, 1200));

  const topic = nodeData.name;

  const title = `## Detailed Analysis of: ${topic}`;

  if (nodeData.isConsolidated) {
    const originalChildrenList = nodeData._originalChildren
      .map((child) => {
        let listItem = `- ${child.name}`;
        if (child.links && child.links.length > 0) {
          listItem += ` ([Source](${child.links[0]}))`;
        }
        return listItem;
      })
      .join('\n');

    return `
## ${nodeData.parentName}
${nodeData.description}

### Full List of Children:
${originalChildrenList}
    `;
  }

  const contextualDescriptions = contextPath
    .filter((item) => item.description)
    .map((item) => {
      let section = `#### ${item.name}\n${item.description}`;
      section += formatLinks(item.links);
      return section;
    })
    .join('\n\n---\n\n');

  const mockContent = `
${title}

${contextualDescriptions.length > 0 ? contextualDescriptions : '_No descriptions available for this path._'}
  `;
  return mockContent;
};

const DetailedAnalysis = React.forwardRef(
  ({ name, description, links }, ref) => {
    const [isExpanded, setIsExpanded] = useState(true);

    if (!description) {
      return null;
    }

    const formattedLinks = formatLinks(links);
    const content = description + formattedLinks;

    return (
      // HIGHLIGHT: The ref is attached to the main div of this component
      <div ref={ref} className="py-4 border-b border-gray-200 last:border-b-0">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex justify-between items-center text-left"
        >
          <h4 className="text-lg font-semibold text-gray-800">{name}</h4>
          <svg
            className={`w-5 h-5 text-gray-500 transform transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M19 9l-7 7-7-7"
            ></path>
          </svg>
        </button>
        {isExpanded && (
          <div className="mt-2 prose prose-slate max-w-none prose-h4:text-base prose-h4:font-semibold">
            <ReactMarkdown>{content}</ReactMarkdown>
          </div>
        )}
      </div>
    );
  }
);

const soberColors = [
  '#BFDBFE',
  '#D1FAE5',
  '#FEF3C7',
  '#FEE2E2',
  '#E5E7EB',
  '#E9D5FF',
  '#A7F3D0',
  '#FDE68A',
];
const color = d3.scaleOrdinal(soberColors);
const EXPANSION_STATE_KEY_PREFIX = 'mindMapExpansionState_';
const TRANSFORM_STATE_KEY_PREFIX = 'mindMapTransformState_';
const SELECTED_NODE_ID_KEY_PREFIX = 'mindMapSelectedNodeId_';
const CONTEXT_PATH_KEY_PREFIX = 'mindMapContextPath_';
const SEARCH_PROPS_KEY_PREFIX = 'mindMapSearchProps_';
const LAST_ACTIVE_MAP_KEY = 'mindMapLastActiveKey';

// --- Main App Component ---
const MindMapReact = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [currentMap, setCurrentMap] = useState({
    key: 'Default',
    data: defaultMindMapData,
  });
  useEffect(() => {
    if (currentMap && currentMap.data && currentMap.data.name) {
      document.title = `Mindmap - ${currentMap.data.name}`;
    }
  }, [currentMap]); // This hook runs whenever 'currentMap' state is updated.

  const [mapIsLoading, setMapIsLoading] = useState(false);
  const [customJson, setCustomJson] = useState('');
  const [jsonError, setJsonError] = useState('');
  const [isCustomJsonVisible, setIsCustomJsonVisible] = useState(false);

  const svgRef = useRef();
  const treeControllerRef = useRef();
  const tooltipRef = useRef();
  const detailsRef = useRef();
  const mapContainerRef = useRef();
  const [markdownContent, setMarkdownContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);

  const [searchProps, setSearchProps] = useState(null);
  const [searchText, setSearchText] = useState('');

  const [detailedAnalysisPath, setDetailedAnalysisPath] = useState([]);
  const [siblingInfo, setSiblingInfo] = useState({ prev: null, next: null });

  const selectedNodeRef = useRef(selectedNode);
  selectedNodeRef.current = selectedNode;

  const clearDetailState = useCallback((dataKey) => {
    setMarkdownContent('');
    setSelectedNode(null);
    setSearchProps(null);
    setSearchText('');
    sessionStorage.removeItem(`${SELECTED_NODE_ID_KEY_PREFIX}${dataKey}`);
    sessionStorage.removeItem(`${CONTEXT_PATH_KEY_PREFIX}${dataKey}`);
    sessionStorage.removeItem(`${SEARCH_PROPS_KEY_PREFIX}${dataKey}`);
  }, []);

  const handleMapSelection = useCallback(
    async (source) => {
      // HIGHLIGHT: Use source.id or a unique key for state management
      const dataKey = source.id || source.url;
      setMapIsLoading(true);
      setIsSidebarOpen(false);
      clearDetailState(dataKey);
      sessionStorage.setItem(LAST_ACTIVE_MAP_KEY, dataKey);

      const newUrl = new URL(window.location);
      if (source.id) {
        newUrl.search = `?id=${source.id}`;
      } else if (source.url) {
        newUrl.search = `?url=${encodeURIComponent(source.url)}`;
      }
      window.history.pushState({}, '', newUrl);

      if (source.data) {
        setCurrentMap({ key: dataKey, data: source.data });
        setMapIsLoading(false);
      } else if (source.url) {
        try {
          const data = await fetchMindMapFromUrl(source.url);
          setCurrentMap({ key: dataKey, data });
        } catch (error) {
          console.error('Failed to fetch mind map:', error);
        } finally {
          setMapIsLoading(false);
        }
      }
    },
    [clearDetailState]
  );

  const handleCustomJsonLoad = () => {
    try {
      const parsedJson = JSON.parse(customJson);
      if (!parsedJson.name || !parsedJson.children) {
        throw new Error(
          "Invalid format. Must have 'name' and 'children' properties."
        );
      }
      setJsonError('');
      // HIGHLIGHT: Use a consistent key for custom maps
      const key = 'Custom';
      clearDetailState(key);

      // HIGHLIGHT: Save the custom JSON and the active key
      sessionStorage.setItem(LAST_ACTIVE_MAP_KEY, key);
      sessionStorage.setItem('mindMapCustomJsonContent', customJson);

      const newUrl = new URL(window.location);
      newUrl.search = '';
      window.history.pushState({}, '', newUrl);

      setCurrentMap({ key, data: parsedJson });
      setIsSidebarOpen(false);
    } catch (error) {
      setJsonError(error.message);
    }
  };

  const handleNodeClick = useCallback(
    async (node, isNavClick = false) => {
      setSearchText(node.data.name);

      if (!node.data.description) {
        if (selectedNodeRef.current) {
          clearDetailState(currentMap.key);
        }
        return;
      }

      const dataKey = currentMap.key;
      if (selectedNodeRef.current?.id === node.id) {
        clearDetailState(dataKey);
        return;
      }
      setIsLoading(true);
      setSelectedNode(node);
      setDetailedAnalysisPath([]);
      setSearchProps(null);

      try {
        const savedSearchPropsJSON = sessionStorage.getItem(
          `${SEARCH_PROPS_KEY_PREFIX}${dataKey}_${node.id}`
        );
        if (savedSearchPropsJSON) {
          const savedSearchProps = JSON.parse(savedSearchPropsJSON);
          setSearchProps(savedSearchProps);
        }

        if (node.parent && node.parent.children) {
          const siblings = node.parent.children;
          const currentIndex = siblings.findIndex((d) => d.id === node.id);
          setSiblingInfo({
            prev: currentIndex > 0 ? siblings[currentIndex - 1] : null,
            next:
              currentIndex < siblings.length - 1
                ? siblings[currentIndex + 1]
                : null,
          });
        } else {
          setSiblingInfo({ prev: null, next: null });
        }

        const ancestors = node.ancestors().reverse();
        const contextPath = ancestors.map((d) => d.data);

        setDetailedAnalysisPath(contextPath);

        sessionStorage.setItem(
          `${SELECTED_NODE_ID_KEY_PREFIX}${dataKey}`,
          node.id
        );
        sessionStorage.setItem(
          `${CONTEXT_PATH_KEY_PREFIX}${dataKey}`,
          JSON.stringify(contextPath)
        );

        // HIGHLIGHT: Only auto-expand if the click came from a navigation button
        if (isNavClick && treeControllerRef.current) {
          const { root, update } = treeControllerRef.current;
          const targetNode = root.descendants().find((d) => d.id === node.id);
          if (targetNode && targetNode._children && !targetNode.children) {
            targetNode.children = targetNode._children;
            update(targetNode);
          }
        }
      } catch (error) {
        console.error('Failed to process node data:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [currentMap.key, clearDetailState]
  );

  const handleSearchInitiate = () => {
    if (!selectedNode) return;
    const props = {
      searchText: searchText,
      searchType: 'vector',
    };
    setSearchProps(props);
    sessionStorage.setItem(
      `${SEARCH_PROPS_KEY_PREFIX}${currentMap.key}_${selectedNode.id}`,
      JSON.stringify(props)
    );
  };

  const handleCollapseAll = () => {
    if (treeControllerRef.current) {
      const { root, update } = treeControllerRef.current;
      root.descendants().forEach((d) => {
        if (d.depth > 0) d.children = null;
      });
      update(root);
    }
  };

  const handleExpandAll = () => {
    if (treeControllerRef.current) {
      const { root, update } = treeControllerRef.current;
      root.descendants().forEach((d) => {
        d.children = d._children;
      });
      update(root);
    }
  };

  const handleDownloadImage = useCallback(() => {
    if (svgRef.current === null) return;
    const svgNode = svgRef.current;
    const gNode = d3.select(svgNode).select('g').node();
    const bounds = gNode.getBBox();
    const padding = 50;
    const width = bounds.width + padding * 2;
    const height = bounds.height + padding * 2;
    const originalTransform = gNode.getAttribute('transform');

    d3.select(svgNode).attr('width', width).attr('height', height);
    d3.select(gNode).attr(
      'transform',
      `translate(${-bounds.x + padding}, ${-bounds.y + padding})`
    );

    htmlToImage
      .toPng(svgNode, {
        width: width,
        height: height,
        backgroundColor: '#f9fafb',
      })
      .then((dataUrl) => {
        const link = document.createElement('a');
        link.download = 'mind-map.png';
        link.href = dataUrl;
        link.click();
      })
      .catch((err) => {
        console.error('Oops, something went wrong!', err);
      })
      .finally(() => {
        d3.select(svgNode).attr('width', '100%').attr('height', '100%');
        d3.select(gNode).attr('transform', originalTransform);
      });
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const url = params.get('url');
    const id = params.get('id');

    if (url) {
      handleMapSelection({ name: 'External URL', url });
    } else if (id) {
      // HIGHLIGHT: Find source by 'id' instead of 'name'
      const source = mindMapSources.find((s) => s.id === id);
      if (source) {
        handleMapSelection(source);
      }
    }
  }, [handleMapSelection]);

  // Place this inside the MindMapReact component, after the other useEffect
  useEffect(() => {
    const lastActiveKey = sessionStorage.getItem(LAST_ACTIVE_MAP_KEY);
    if (lastActiveKey === 'Custom') {
      const savedJson = sessionStorage.getItem('mindMapCustomJsonContent');
      if (savedJson) {
        try {
          const parsedJson = JSON.parse(savedJson);
          setCurrentMap({ key: 'Custom', data: parsedJson });
        } catch (e) {
          console.error('Failed to parse saved custom JSON', e);
        }
      }
    }
  }, []); // Empty array ensures this runs only once on mount

  useEffect(() => {
    const data = currentMap.data;
    const dataKey = currentMap.key;
    if (!svgRef.current || !data) return;

    const dataCopy = JSON.parse(JSON.stringify(data));

    const EXPANSION_STATE_KEY = `${EXPANSION_STATE_KEY_PREFIX}${dataKey}`;
    const TRANSFORM_STATE_KEY = `${TRANSFORM_STATE_KEY_PREFIX}${dataKey}`;
    const SELECTED_NODE_ID_KEY = `${SELECTED_NODE_ID_KEY_PREFIX}${dataKey}`;
    const CONTEXT_PATH_KEY = `${CONTEXT_PATH_KEY_PREFIX}${dataKey}`;
    let savedExpansionState = {};
    let savedTransform = null;
    let savedSelectedNodeId = null;
    let savedContextPath = null;
    let savedSearchProps = null;

    try {
      const expansionJSON = sessionStorage.getItem(EXPANSION_STATE_KEY);
      if (expansionJSON) savedExpansionState = JSON.parse(expansionJSON);
      const transformJSON = sessionStorage.getItem(TRANSFORM_STATE_KEY);
      if (transformJSON) {
        const t = JSON.parse(transformJSON);
        savedTransform = d3.zoomIdentity.translate(t.x, t.y).scale(t.k);
      }
      savedSelectedNodeId = sessionStorage.getItem(SELECTED_NODE_ID_KEY);
      const contextPathJSON = sessionStorage.getItem(CONTEXT_PATH_KEY);
      if (contextPathJSON) savedContextPath = JSON.parse(contextPathJSON);

      if (savedSelectedNodeId) {
        const SEARCH_PROPS_KEY = `${SEARCH_PROPS_KEY_PREFIX}${dataKey}_${savedSelectedNodeId}`;
        const searchPropsJSON = sessionStorage.getItem(SEARCH_PROPS_KEY);
        if (searchPropsJSON) savedSearchProps = JSON.parse(searchPropsJSON);
      }
    } catch (e) {
      console.error('Could not parse the saved state.', e);
    }

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();
    const width = svgRef.current.clientWidth;
    const height = svgRef.current.clientHeight;
    const g = svg.append('g');

    const tooltip = d3.select(tooltipRef.current);

    const zoom = d3
      .zoom()
      .scaleExtent([0.1, 2])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
        try {
          sessionStorage.setItem(
            TRANSFORM_STATE_KEY,
            JSON.stringify(event.transform)
          );
        } catch (e) {
          console.error('Could not save transform state.', e);
        }
      });
    svg.call(zoom);

    const isMobile = width < 768;
    const horizontalSpacing = isMobile ? 280 : 360;
    const treeLayout = d3.tree().nodeSize([100, horizontalSpacing]);
    const root = d3.hierarchy(dataCopy, (d) => d.children);

    root.descendants().forEach((d, i) => {
      d.id = i;
      d._children = d.children;
      const isExpanded = savedExpansionState[d.id];
      if (Object.keys(savedExpansionState).length > 0) {
        if (d._children && !isExpanded) d.children = null;
      } else {
        if (d.depth > 0) {
          d.children = null;
        }
      }
      if (savedSelectedNodeId && d.id === parseInt(savedSelectedNodeId, 10)) {
        setSelectedNode(d);
        setSearchText(
          savedSearchProps ? savedSearchProps.searchText : d.data.name
        );
        setDetailedAnalysisPath(savedContextPath || []);
        setSearchProps(savedSearchProps);
      }
    });

    const wrap = (text) => {
      // HIGHLIGHT: Chooses a different max width based on screen size
      const nodeWidth = isMobile ? 220 : 360;
      const padding = 40;

      text.each(function () {
        const textEl = d3.select(this);
        const words = textEl.text().split(/\s+/).reverse();
        let word;
        let line = [];
        const lineHeight = 1.2;

        textEl.text(null); // Clear original text

        // First line
        let tspan = textEl.append('tspan').attr('x', 0);

        while ((word = words.pop())) {
          line.push(word);
          tspan.text(line.join(' '));
          if (
            tspan.node().getComputedTextLength() > nodeWidth - padding &&
            line.length > 1
          ) {
            line.pop();
            tspan.text(line.join(' '));
            words.push(word);
            break;
          }
        }

        // Second line (if necessary)
        if (words.length > 0) {
          line = [];
          tspan = textEl
            .append('tspan')
            .attr('x', 0)
            .attr('dy', `${lineHeight}em`);
          while ((word = words.pop())) {
            line.push(word);
            tspan.text(line.join(' '));
            if (
              tspan.node().getComputedTextLength() > nodeWidth - padding &&
              line.length > 1
            ) {
              line.pop();
              tspan.text(line.join(' '));
              break;
            }
          }
        }

        // Vertical centering logic
        const numLines = textEl.selectAll('tspan').size();
        const yOffset = -(numLines - 1) * 8;
        textEl.attr('y', yOffset);
      });
    };

    const update = (source) => {
      const duration = 500;
      const treeData = treeLayout(root);
      const nodes = treeData.descendants();
      const links = treeData.descendants().slice(1);

      const currentExpansionState = {};
      root.descendants().forEach((d) => {
        if (d.children) currentExpansionState[d.id] = true;
      });
      try {
        sessionStorage.setItem(
          EXPANSION_STATE_KEY,
          JSON.stringify(currentExpansionState)
        );
      } catch (e) {
        console.error('Could not save expansion state.', e);
      }

      const link = g.selectAll('path.link').data(links, (d) => d.id);
      const linkEnter = link
        .enter()
        .insert('path', 'g')
        .attr('class', 'link')
        .attr('d', (d) => {
          const o = { x: source.x0 || root.x0, y: source.y0 || root.y0 };
          return d3
            .linkHorizontal()
            .x((node) => node.y)
            .y((node) => node.x)({ source: o, target: o });
        })
        .style('fill', 'none')
        .style('stroke', '#9CA3AF')
        .style('stroke-width', '2px');
      const linkUpdate = linkEnter.merge(link);
      linkUpdate
        .transition()
        .duration(duration)
        .attr('d', (d) =>
          d3
            .linkHorizontal()
            .x((node) => node.y)
            .y((node) => node.x)({ source: d.parent, target: d })
        );
      link
        .exit()
        .transition()
        .duration(duration)
        .attr('d', (d) => {
          const o = { x: source.x, y: source.y };
          return d3
            .linkHorizontal()
            .x((node) => node.y)
            .y((node) => node.x)({ source: o, target: o });
        })
        .remove();

      const node = g.selectAll('g.node').data(nodes, (d) => d.id);
      const nodeEnter = node
        .enter()
        .append('g')
        .attr('class', 'node')
        .attr(
          'transform',
          (d) => `translate(${source.y0 || root.y0},${source.x0 || root.x0})`
        )
        .on('mouseover', (event, d) => {
          if (isMobile) return;
          if (d.data.description) {
            const tooltipNode = tooltipRef.current;
            tooltip.classed('opacity-0', false).html(d.data.description);
            let left = event.clientX + 15;
            if (left + tooltipNode.offsetWidth > window.innerWidth) {
              left = event.clientX - tooltipNode.offsetWidth - 15;
            }
            tooltip
              .style('left', left + 'px')
              .style('top', event.clientY + 15 + 'px');
          }
        })
        .on('mousemove', (event, d) => {
          if (d.data.description) {
            const tooltipNode = tooltipRef.current;
            let left = event.clientX + 15;
            if (left + tooltipNode.offsetWidth > window.innerWidth) {
              left = event.clientX - tooltipNode.offsetWidth - 15;
            }
            tooltip
              .style('left', left + 'px')
              .style('top', event.clientY + 15 + 'px');
          }
        })
        .on('mouseout', () => {
          tooltip.classed('opacity-0', true);
        })
        .on('click', (event, d) => {
          if (d._children && !d.children) {
            const currentZoom = d3.zoomTransform(svg.node());
            const targetX = width / 4;
            const targetY = height / 2;
            const newX = targetX - d.y * currentZoom.k;
            const newY = targetY - d.x * currentZoom.k;
            const newTransform = d3.zoomIdentity
              .translate(newX, newY)
              .scale(currentZoom.k);
            svg
              .transition()
              .duration(duration)
              .call(zoom.transform, newTransform);
          }
          if (d.children || d._children) {
            d.children = d.children ? null : d._children;
            update(d);
          }
          if (d.data.description) {
            handleNodeClick(d);
          }
        });

      nodeEnter.append('rect').attr('rx', 10).style('stroke-width', 2);

      nodeEnter
        .append('text')
        .text((d) => d.data.name)
        .attr('text-anchor', 'middle')
        .style('font-size', '16px')
        .style('font-weight', '500')
        .style('pointer-events', 'none')
        .call(wrap);

      const nodeUpdate = node.merge(nodeEnter);
      nodeUpdate
        .transition()
        .duration(duration)
        .attr('transform', (d) => `translate(${d.y},${d.x})`);

      nodeUpdate
        .select('rect')
        .attr('width', function (d) {
          const textWidth = d3
            .select(this.parentNode)
            .select('text')
            .node()
            .getBBox().width;
          return Math.max(240, Math.min(textWidth + 40, 360));
        })
        .attr('x', function (d) {
          const width = d3.select(this).attr('width');
          return -width / 2;
        })
        .attr('height', function (d) {
          const numLines = d3
            .select(this.parentNode)
            .select('text')
            .selectAll('tspan')
            .size();
          return numLines > 1 ? 60 : 40;
        })
        .attr('y', function (d) {
          const numLines = d3
            .select(this.parentNode)
            .select('text')
            .selectAll('tspan')
            .size();
          return -(numLines > 1 ? 60 : 40) / 2;
        })
        .transition()
        .duration(duration)
        .style('fill', (d) => {
          const isLeaf = !d._children && !d.children;
          return isLeaf ? '#fff' : color(d.depth % soberColors.length);
        })
        .style('stroke', (d) => {
          const isLeaf = !d._children && !d.children;
          return isLeaf
            ? '#D1D5DB'
            : d3.rgb(color(d.depth % soberColors.length)).darker(0.3);
        });

      nodeUpdate
        .select('text')
        .transition()
        .duration(duration)
        .style('fill', '#374151');
      node
        .exit()
        .transition()
        .duration(duration)
        .attr('transform', (d) => `translate(${source.y},${source.x})`)
        .remove();
      nodes.forEach((d) => {
        d.x0 = d.x;
        d.y0 = d.y;
      });
    };

    root.x0 = height / 2;
    root.y0 = 0;
    update(root);
    treeControllerRef.current = { root, update };
    const initialTranslateX = isMobile ? 50 : width / 8;
    const initialTransform =
      savedTransform ||
      d3.zoomIdentity.translate(initialTranslateX, height / 2).scale(0.8);
    svg.call(zoom.transform, initialTransform);
  }, [currentMap, handleNodeClick]);

  useEffect(() => {
    if (!svgRef.current || !d3.select(svgRef.current).node()) return;
    const g = d3.select(svgRef.current).select('g');
    g.selectAll('g.node').each(function (d) {
      const node = d3.select(this);
      const isSelected = selectedNode && d.id === selectedNode.id;
      node
        .select('rect')
        .transition()
        .duration(200)
        .style(
          'fill',
          isSelected
            ? '#10B981'
            : d._children
              ? color(d.depth % soberColors.length)
              : '#fff'
        )
        .style(
          'stroke',
          isSelected
            ? '#047857'
            : d._children
              ? d3.rgb(color(d.depth % soberColors.length)).darker(0.3)
              : '#D1D5DB'
        );
      node
        .select('text')
        .transition()
        .duration(200)
        .style('fill', isSelected ? 'white' : '#374151');
    });
  }, [selectedNode]);

  useEffect(() => {
    if (
      detailedAnalysisPath.length > 0 &&
      !isLoading &&
      !selectedNode._children
    ) {
      detailsRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [detailedAnalysisPath, isLoading]);

  return (
    <div className="bg-gray-100 min-h-screen w-full sm:p-4 font-sans">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;700&display=swap'); body { font-family: 'Inter', sans-serif; }`}</style>

      <div
        ref={tooltipRef}
        className="fixed z-50 p-3 text-sm text-gray-800 bg-white rounded-lg shadow-xl pointer-events-none opacity-0 transition-opacity duration-200 max-w-xs border border-gray-200 text-justify"
      ></div>

      <div className="max-w-7xl w-full min-h-[95vh] mx-auto bg-white rounded-xl shadow-lg overflow-hidden flex flex-col relative">
        <div
          className={`absolute top-0 left-0 h-full bg-white shadow-2xl z-30 transform transition-transform duration-300 ease-in-out ${
            isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
          } w-full md:w-1/2 border-r border-gray-200`}
        >
          <div className="p-4 flex justify-between items-center border-b bg-slate-50">
            <h2 className="text-lg font-semibold text-slate-700">
              Choose a Mind Map
            </h2>
            <button
              onClick={() => setIsSidebarOpen(false)}
              className="p-1 rounded-full hover:bg-slate-200"
            >
              <svg
                className="w-6 h-6 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                ></path>
              </svg>
            </button>
          </div>
          {/* HIGHLIGHT: Responsive padding on sidebar items and adjusted height calculation */}
          <div className="overflow-y-auto h-[calc(100%-61px)] p-2 sm:p-5 space-y-2 sm:space-y-4">
            {mindMapSources.map((source) => (
              <div
                key={source.id}
                onClick={() => handleMapSelection(source)}
                className="p-2 sm:p-4 border rounded-lg hover:bg-emerald-50 hover:border-emerald-300 cursor-pointer transition-colors"
              >
                <h3 className="font-bold text-lg text-emerald-800">
                  {source.name}
                </h3>
                <div className="prose prose-sm mt-1 text-gray-600">
                  <ReactMarkdown>{source.description}</ReactMarkdown>
                </div>
              </div>
            ))}
            <div>
              <button
                onClick={() => setIsCustomJsonVisible(!isCustomJsonVisible)}
                className="font-semibold text-lg text-gray-700 w-full text-left flex justify-between items-center py-2 px-2 sm:px-0"
              >
                Load from Custom JSON
                <svg
                  className={`w-5 h-5 transition-transform duration-200 ${
                    isCustomJsonVisible ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 9l-7 7-7-7"
                  ></path>
                </svg>
              </button>
              {isCustomJsonVisible && (
                <div className="mt-2 space-y-2 px-2 sm:px-0">
                  <textarea
                    value={customJson}
                    onChange={(e) => setCustomJson(e.target.value)}
                    className="w-full h-40 p-2 border rounded-md font-mono text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Paste your JSON here..."
                  ></textarea>
                  {jsonError && (
                    <p className="text-red-500 text-sm">{jsonError}</p>
                  )}
                  <button
                    onClick={handleCustomJsonLoad}
                    className="w-full px-4 py-2 bg-emerald-600 text-white font-semibold rounded-md hover:bg-emerald-700 transition-colors"
                  >
                    Load Custom Map
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex-shrink-0 p-4 border-b flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
          <button
            onClick={() => setIsSidebarOpen(true)}
            className="flex items-center px-4 py-2 bg-white text-gray-700 text-sm font-semibold rounded-md shadow-sm border border-gray-300 hover:bg-gray-50"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 6h16M4 12h16M4 18h16"
              ></path>
            </svg>
            Choose Mind Map
          </button>
          <div className="ml-4">
            <h1 className="text-xl font-bold text-gray-900">
              {currentMap.data.name}
            </h1>
          </div>
        </div>
        <div className="flex-grow relative">
          {mapIsLoading ? (
            <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-20">
              <p className="text-lg font-semibold">Loading new mind map...</p>
            </div>
          ) : (
            <div className="w-full flex flex-col antialiased text-gray-800">
              <div
                ref={mapContainerRef}
                className="h-[70vh] relative border-b-2 border-gray-200"
              >
                <div className="h-[70vh] relative border-b-2 border-gray-200">
                  <div className="absolute top-4 right-4 z-10 flex space-x-2">
                    <button
                      onClick={handleDownloadImage}
                      className="px-3 py-1.5 bg-white text-gray-700 text-sm font-semibold rounded-md shadow-sm border border-gray-300 hover:bg-gray-50"
                    >
                      Download
                    </button>
                    <button
                      onClick={handleExpandAll}
                      className="px-3 py-1.5 bg-white text-gray-700 text-sm font-semibold rounded-md shadow-sm border border-gray-300 hover:bg-gray-50"
                    >
                      Expand All
                    </button>
                    <button
                      onClick={handleCollapseAll}
                      className="px-3 py-1.5 bg-white text-gray-700 text-sm font-semibold rounded-md shadow-sm border border-gray-300 hover:bg-gray-50"
                    >
                      Collapse All
                    </button>
                  </div>
                  <svg
                    ref={svgRef}
                    className="w-full h-full bg-slate-100"
                  ></svg>
                </div>
              </div>
              <div className="bg-white overflow-y-auto">
                {isLoading && (
                  <div className="flex items-center justify-center h-full py-10">
                    <div className="flex flex-col items-center">
                      <svg
                        className="animate-spin -ml-1 mr-3 h-8 w-8 text-emerald-500"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      <p className="mt-2 text-gray-600">Fetching details...</p>
                    </div>
                  </div>
                )}
                {!isLoading && detailedAnalysisPath.length > 0 && (
                  <div className="p-4 sm:p-8 ">
                    <>
                      <h2 className="text-2xl font-bold text-gray-900 mb-4">
                        Detailed Analysis
                      </h2>
                      {detailedAnalysisPath.map((nodeData, index) => (
                        <DetailedAnalysis
                          key={index}
                          ref={
                            index === detailedAnalysisPath.length - 1
                              ? detailsRef
                              : null
                          }
                          name={nodeData.name}
                          description={nodeData.description}
                          links={nodeData.links}
                        />
                      ))}
                      <hr className="my-8" />
                    </>
                  </div>
                )}
                {detailedAnalysisPath.length > 0 && (
                  <div className="mt-8 flex justify-center items-center">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleNodeClick(siblingInfo.prev, true)}
                        disabled={!siblingInfo.prev}
                        className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        aria-label="Previous Sibling"
                      >
                        <svg
                          className="w-5 h-5 text-gray-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M15 19l-7-7 7-7"
                          ></path>
                        </svg>
                      </button>
                      <button
                        onClick={() => {
                          mapContainerRef.current?.scrollIntoView({
                            behavior: 'smooth',
                          });
                        }}
                        className="text-sm text-emerald-600 hover:text-emerald-800 transition-colors font-semibold"
                      >
                        ↑ Scroll to Map
                      </button>
                      <button
                        onClick={() => handleNodeClick(siblingInfo.next, true)}
                        disabled={!siblingInfo.next}
                        className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        aria-label="Next Sibling"
                      >
                        <svg
                          className="w-5 h-5 text-gray-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M9 5l7 7-7 7"
                          ></path>
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
                {!isLoading && detailedAnalysisPath.length === 0 && (
                  <div className="flex items-center justify-center h-full text-center text-gray-500 py-10">
                    <div>
                      <h3 className="text-xl font-semibold">
                        Welcome to the Mind Map Explorer
                      </h3>
                      <p className="mt-2">
                        Expand nodes to explore. Click a node with a description
                        for details!
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      <hr className="my-8" />
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-2">
          Continue Exploring
        </h3>
        <p className="text-sm text-gray-500 mb-3">
          Add words from above to search and read more on this topic
        </p>
        <div className="flex items-center space-x-2">
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearchInitiate()}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-emerald-500 focus:border-emerald-500"
          />
          <button
            onClick={handleSearchInitiate}
            className="px-4 py-2 bg-emerald-600 text-white font-semibold rounded-md hover:bg-emerald-700 transition-colors hidden sm:block"
          >
            Search
          </button>
        </div>
      </div>
      {searchProps && <Search {...searchProps} />}
    </div>
  );
};

export default MindMapReact;
